import { StringDevOverlayModule, OverlayBaseMetrics } from "../core/StringDevOverlayModule";
import { resolveDevtoolsIcon } from "../core/StringDevIconRegistry";
import { StringObject } from "../../../objects/StringObject";
import { EasingFunctionOutput } from "../../../tools/EasingFunctionTool";
import { styleTxn } from "../../../utils/style-txn";
import { STRING_DEV_PROGRESS_OVERRIDE_KEY } from "../../scroll/StringProgress";
import { PROGRESS_CSS } from "./StringDevProgress.css";

interface ProgressEntry {
  object: StringObject;
  chrome: HTMLDivElement;
  badge: HTMLButtonElement;
  panel: HTMLDivElement;
  title: HTMLSpanElement;
  slider: HTMLInputElement;
  easedValue: HTMLSpanElement;
  rawValue: HTMLSpanElement;
  rangeValue: HTMLSpanElement;
  onBadgeClick: (event: MouseEvent) => void;
  onSliderInput: () => void;
}

interface ProgressMeasurement {
  visible: boolean;
  docX: number;
  docY: number;
  targetLabel: string;
  progress: number;
  rawProgress: number;
  startPosition: number;
  endPosition: number;
  currentScroll: number;
  key: string;
}

function clampProgress(raw: number): number {
  if (!Number.isFinite(raw)) return 0;
  if (raw <= 0) return 0;
  if (raw >= 1) return 1;
  const epsilon = 1e-4;
  if (raw > 1 - epsilon) return 1;
  if (raw < epsilon) return 0;
  return raw;
}

function formatPercent(value: number): string {
  const percent = clampProgress(value) * 100;
  return `${Math.round(percent * 10) / 10}%`;
}

function formatNumber(value: number): string {
  if (!Number.isFinite(value)) {
    return "0";
  }

  const rounded = Math.round(value * 10) / 10;
  if (Object.is(rounded, -0)) {
    return "0";
  }

  return Number.isInteger(rounded) ? String(rounded) : rounded.toFixed(1);
}

export class StringDevProgress extends StringDevOverlayModule<ProgressEntry, ProgressMeasurement> {
  static override devtool = {
    id: "progress",
    label: "Progress",
    icon: "progress",
    order: 35,
    styles: PROGRESS_CSS,
    overlay: {
      layerName: "devtools-progress",
      zIndex: 10013,
      layerAttribute: "data-string-progress-layer",
      overlayId: "progress",
    },
  };

  private openEntryId: string | null = null;
  private readonly onDocumentPointerDownBind = (event: PointerEvent) => {
    this.onDocumentPointerDown(event);
  };

  constructor(context: any) {
    super(context);
    this.htmlKey = "progress";
    this.attributesToMap = [
      ...this.attributesToMap,
      { key: "easing", type: "easing", fallback: this.settings["easing"] },
    ];
  }

  onInit(): void {
    super.onInit();
    document.addEventListener("pointerdown", this.onDocumentPointerDownBind);
  }

  destroy(): void {
    document.removeEventListener("pointerdown", this.onDocumentPointerDownBind);
    this.clearAllProgressOverrides();
    this.openEntryId = null;
    super.destroy();
  }

  protected createOverlayEntry(object: StringObject): ProgressEntry {
    const targetLabel = this.resolveOverlayTargetId(object);
    const chrome = document.createElement("div");
    chrome.setAttribute("data-string-devtools-overlay-chrome", "");
    chrome.setAttribute("data-string-devtools-theme", "");
    chrome.setAttribute("data-target-id", targetLabel);
    chrome.setAttribute("hidden", "");
    chrome.setAttribute("string-isolation", "");
    chrome.setAttribute("data-string-isolation", "");

    const badge = this.createOverlayBadge({
      targetId: targetLabel,
      selectorAttribute: "data-string-progress-badge",
      attributes: {
        "data-open": "false",
        "aria-label": `Open progress details for ${targetLabel}`,
      },
    });
    this.applyOverlayBadgeState(badge, {
      html: resolveDevtoolsIcon("progress"),
    });

    const panel = document.createElement("div");
    panel.setAttribute("data-string-devtools-overlay-panel", "");
    panel.setAttribute("data-string-devtools-panel", "");
    panel.setAttribute("data-string-progress-panel", "");
    panel.setAttribute("data-open", "false");
    panel.setAttribute("data-target-id", targetLabel);
    panel.setAttribute("string-isolation", "");
    panel.setAttribute("data-string-isolation", "");

    const header = document.createElement("div");
    header.setAttribute("data-string-devtools-panel-header", "");

    const spacer = document.createElement("span");
    spacer.setAttribute("data-string-devtools-panel-icon", "");
    spacer.innerHTML = resolveDevtoolsIcon("progress");

    const title = document.createElement("span");
    title.setAttribute("data-string-devtools-panel-title", "");
    title.textContent = targetLabel;

    const closeButton = document.createElement("button");
    closeButton.type = "button";
    closeButton.setAttribute("data-string-devtools-icon-button", "");
    closeButton.setAttribute("aria-label", `Close progress details for ${targetLabel}`);
    closeButton.innerHTML = `
      <svg viewBox="0 0 16 16" aria-hidden="true">
        <path d="M2 2L14 14" fill="none" stroke="currentColor"/>
        <path d="M2 14L14 2" fill="none" stroke="currentColor"/>
      </svg>
    `;
    closeButton.addEventListener("click", (event) => {
      event.stopPropagation();
      this.closePanel(targetLabel);
    });

    header.appendChild(spacer);
    header.appendChild(title);
    header.appendChild(closeButton);

    const summary = document.createElement("div");
    summary.setAttribute("data-string-progress-summary", "");
    summary.innerHTML = `
      <div data-string-progress-control>
        <input
          type="range"
          min="0"
          max="1000"
          step="1"
          value="0"
          aria-label="Preview progress for ${targetLabel}"
          data-string-progress-slider
        />
      </div>
      <div data-string-progress-meta>
        <div data-string-progress-row>
          <span data-string-progress-label>Eased</span>
          <span data-string-progress-value data-field="eased">0%</span>
        </div>
        <div data-string-progress-row>
          <span data-string-progress-label>Raw</span>
          <span data-string-progress-value data-field="raw">0%</span>
        </div>
        <div data-string-progress-row>
          <span data-string-progress-label>Range</span>
          <span data-string-progress-value data-field="range">0 -> 0</span>
        </div>
      </div>
    `;

    const onBadgeClick = (event: MouseEvent) => {
      event.stopPropagation();
      event.preventDefault();
      if (this.openEntryId === targetLabel) {
        this.closePanel(targetLabel);
        return;
      }
      this.openPanel(targetLabel);
    };

    const slider = summary.querySelector("[data-string-progress-slider]") as HTMLInputElement;
    const onSliderInput = () => {
      const rawProgress = clampProgress(Number(slider.value) / 1000);
      this.setEntryProgressOverride(object, rawProgress);
    };

    badge.addEventListener("click", onBadgeClick);
    slider.addEventListener("input", onSliderInput);
    panel.addEventListener("pointerdown", (event) => event.stopPropagation());

    panel.appendChild(header);
    panel.appendChild(summary);
    chrome.appendChild(badge);
    chrome.appendChild(panel);

    this.viewportLayer.getWorldElement(this.getWorldHost()).appendChild(chrome);

    this.applyEntryEnabledState(chrome);

    return {
      object,
      chrome,
      badge,
      panel,
      title,
      slider,
      easedValue: summary.querySelector('[data-field="eased"]') as HTMLSpanElement,
      rawValue: summary.querySelector('[data-field="raw"]') as HTMLSpanElement,
      rangeValue: summary.querySelector('[data-field="range"]') as HTMLSpanElement,
      onBadgeClick,
      onSliderInput,
    };
  }

  protected destroyOverlayEntry(entry: ProgressEntry): void {
    const targetLabel = entry.object.htmlElement.id || entry.object.id;
    if (this.openEntryId === targetLabel) {
      this.clearEntryProgressOverride(entry.object);
      this.openEntryId = null;
    }

    entry.badge.removeEventListener("click", entry.onBadgeClick);
    entry.slider.removeEventListener("input", entry.onSliderInput);
    entry.chrome.remove();
  }

  protected measureEntry(entry: ProgressEntry, metrics: OverlayBaseMetrics): ProgressMeasurement {
    const object = entry.object;
    const element = object.htmlElement;
    const targetLabel = element.id || object.id;
    const currentScroll = this.data.scroll.transformedCurrent;
    const key = object.getProperty<string>("key") || "--progress";

    const startPosition =
      object.getProperty<number>("start-position") ??
      object.getProperty<number>("progress-start-position") ??
      0;
    const differencePosition =
      object.getProperty<number>("difference-position") ??
      object.getProperty<number>("progress-difference-position") ??
      0;
    const endPosition =
      object.getProperty<number>("end-position") ?? startPosition + differencePosition;

    const rawProgress = this.resolveEntryRawProgress(object, currentScroll);
    const progress = this.resolveEasedProgress(object, rawProgress);

    if (!metrics.visible) {
      return {
        visible: false,
        docX: 0,
        docY: 0,
        targetLabel,
        progress: 0,
        rawProgress: 0,
        startPosition: 0,
        endPosition: 0,
        currentScroll,
        key,
      };
    }

    return {
      visible: true,
      docX: metrics.baseAnchorX,
      docY: metrics.baseAnchorY + metrics.collisionOffset,
      targetLabel,
      progress,
      rawProgress,
      startPosition,
      endPosition,
      currentScroll,
      key,
    };
  }

  protected applyMeasurement(
    entry: ProgressEntry,
    measurement: ProgressMeasurement | undefined,
  ): void {
    if (!measurement || !measurement.visible) {
      entry.chrome.setAttribute("hidden", "");
      return;
    }

    entry.chrome.removeAttribute("hidden");
    styleTxn.setProps(entry.chrome, {
      transform: `translate3d(${Math.round(measurement.docX * 100) / 100}px, ${Math.round(measurement.docY * 100) / 100}px, 0)`,
    });

    styleTxn.setVar(
      entry.slider,
      "--string-progress-slider-fill",
      `${Math.round(clampProgress(measurement.rawProgress) * 1000) / 10}%`,
    );
    const sliderValue = String(Math.round(clampProgress(measurement.rawProgress) * 1000));
    if (entry.slider.value !== sliderValue) {
      entry.slider.value = sliderValue;
    }

    entry.title.textContent = measurement.targetLabel;
    entry.easedValue.textContent = formatPercent(measurement.progress);
    entry.rawValue.textContent = formatPercent(measurement.rawProgress);
    entry.rangeValue.textContent = `${formatNumber(measurement.startPosition)} -> ${formatNumber(measurement.endPosition)}`;

    entry.badge.title = `${measurement.targetLabel}: ${formatPercent(measurement.progress)}`;
  }

  protected onEnabledChange(enabled: boolean): void {
    for (const entry of this.entries.values()) {
      this.applyEntryEnabledState(entry.chrome);
    }

    if (!enabled) {
      this.closeAllPanels();
      this.clearAllProgressOverrides();
    }
  }

  private applyEntryEnabledState(chrome: HTMLDivElement): void {
    chrome.style.display = this.enabled ? "" : "none";
  }

  private openPanel(targetLabel: string): void {
    this.closeAllPanels();
    this.openEntryId = targetLabel;
    for (const entry of this.entries.values()) {
      if ((entry.object.htmlElement.id || entry.object.id) !== targetLabel) {
        continue;
      }
      entry.badge.setAttribute("data-open", "true");
      entry.panel.setAttribute("data-open", "true");
      break;
    }
  }

  private closePanel(targetLabel: string): void {
    for (const entry of this.entries.values()) {
      if ((entry.object.htmlElement.id || entry.object.id) !== targetLabel) {
        continue;
      }
      entry.badge.setAttribute("data-open", "false");
      entry.panel.setAttribute("data-open", "false");
      this.clearEntryProgressOverride(entry.object);
      break;
    }

    if (this.openEntryId === targetLabel) {
      this.openEntryId = null;
    }
  }

  private closeAllPanels(): void {
    for (const entry of this.entries.values()) {
      entry.badge.setAttribute("data-open", "false");
      entry.panel.setAttribute("data-open", "false");
      this.clearEntryProgressOverride(entry.object);
    }
    this.openEntryId = null;
  }

  private onDocumentPointerDown(event: PointerEvent): void {
    if (!this.openEntryId) {
      return;
    }

    const target = event.target;
    if (!(target instanceof Node)) {
      return;
    }

    for (const entry of this.entries.values()) {
      if (entry.chrome.contains(target)) {
        return;
      }
    }

    this.closeAllPanels();
  }

  private resolveEntryRawProgress(object: StringObject, currentScroll: number): number {
    const overrideRawProgress = object.getProperty<number | null>(STRING_DEV_PROGRESS_OVERRIDE_KEY);
    if (overrideRawProgress != null) {
      return clampProgress(overrideRawProgress);
    }

    const startPosition =
      object.getProperty<number>("start-position") ??
      object.getProperty<number>("progress-start-position") ??
      0;
    const differencePosition =
      object.getProperty<number>("difference-position") ??
      object.getProperty<number>("progress-difference-position") ??
      0;
    return clampProgress(
      differencePosition === 0 ? 0 : (currentScroll - startPosition) / differencePosition,
    );
  }

  private resolveEasedProgress(object: StringObject, rawProgress: number): number {
    const easing = object.getProperty<EasingFunctionOutput>("easing");
    return typeof easing === "function" ? clampProgress(easing(rawProgress)) : rawProgress;
  }

  private setEntryProgressOverride(object: StringObject, rawProgress: number): void {
    object.setProperty(STRING_DEV_PROGRESS_OVERRIDE_KEY, clampProgress(rawProgress));
    this.applyObjectProgress(object, rawProgress);
    this.scheduleFullSync();
  }

  private clearEntryProgressOverride(object: StringObject): void {
    if (object.getProperty<number | null>(STRING_DEV_PROGRESS_OVERRIDE_KEY) == null) {
      return;
    }

    object.setProperty<number | null>(STRING_DEV_PROGRESS_OVERRIDE_KEY, null);
    const liveRawProgress = this.resolveEntryRawProgress(
      object,
      this.data.scroll.transformedCurrent,
    );
    this.applyObjectProgress(object, liveRawProgress);
    this.scheduleFullSync();
  }

  private clearAllProgressOverrides(): void {
    for (const entry of this.entries.values()) {
      entry.object.setProperty<number | null>(STRING_DEV_PROGRESS_OVERRIDE_KEY, null);
    }
  }

  private applyObjectProgress(object: StringObject, rawProgress: number): void {
    const normalizedRawProgress = clampProgress(rawProgress);
    const easing = object.getProperty<EasingFunctionOutput>("easing");
    const progress =
      typeof easing === "function"
        ? clampProgress(easing(normalizedRawProgress))
        : normalizedRawProgress;
    const key = object.getProperty<string>("key");

    object.setProperty("progress-raw", normalizedRawProgress);
    object.setProperty("progress-value", progress);
    object.setProperty<number>("progress-applied", progress);

    const eventName =
      object.getProperty<string>("event-progress-name") ??
      object.getScopedEventName("object:progress");
    object.setProperty("event-progress-name", eventName);
    if (eventName) {
      this.events.emit(eventName, progress);
    }

    this.tools.styleTxn.run(() => {
      if (key && object.getProperty("self-disable") !== true) {
        this.tools.styleTxn.setVar(object.htmlElement, key, progress);
      }

      for (const mirror of object.mirrorObjects) {
        const mirrorProgress = mirror.applyProgress(
          normalizedRawProgress,
          typeof easing === "function" ? easing : undefined,
        );
        mirror.setProperty("progress", mirrorProgress);
        if (key) {
          this.tools.styleTxn.setVar(mirror.htmlElement, key, mirrorProgress);
        }
      }
    });
  }
}

export { StringDevProgress as StringProgress };
